import axios from 'axios'

import { apiUrl } from '@/lib/misc/Utils'

export const ACCESS_TOKEN_KEY = 'skey'
export const REFRESH_TOKEN_KEY = 'rekey'
export const CUSTOMER_ID_KEY = 'customerId'

const REFRESH_PATH = '/api/v1/auth/refresh'
const LOGIN_PATH = '/api/v1/auth/login'

let refreshPromise: Promise<string> | null = null

export interface TokenPair {
  access: string
  refresh?: string
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

export function persistTokens(tokens: TokenPair): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access)
  if (tokens.refresh) {
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh)
  }
}

export function clearAuthStorage(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(CUSTOMER_ID_KEY)
}

const AUTH_PAGE_PATTERN = /^\/(login|sign-up)(\/|$|\?)/i

export function isAuthPagePath(path: string): boolean {
  return AUTH_PAGE_PATTERN.test(path)
}

export function safeNextPath(next: string | null | undefined): string | null {
  if (!next || !next.startsWith('/')) return null
  return isAuthPagePath(next) ? null : next
}

export function loginUrlForPath(path: string): string {
  return path === '/' || isAuthPagePath(path)
    ? '/login'
    : `/login?next=${encodeURIComponent(path)}`
}

export function currentPathWithoutNext(): string {
  const url = new URL(window.location.href)
  url.searchParams.delete('next')
  return url.pathname + url.search
}

export function sessionExpired(): void {
  clearAuthStorage()
  window.location.href = loginUrlForPath(currentPathWithoutNext())
}

export function getJwtExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

export function isJwtExpired(token: string, skewSeconds = 60): boolean {
  const exp = getJwtExp(token)
  if (!exp) {
    return true
  }
  return Date.now() / 1000 >= exp - skewSeconds
}

function parseRefreshResponse(data: {
  access?: string
  access_token?: string
  refresh?: string
  refresh_token?: string
}): TokenPair {
  const access = data.access ?? data.access_token
  if (!access) {
    throw new Error('Refresh response missing access token')
  }
  const refresh = data.refresh ?? data.refresh_token
  return refresh ? { access, refresh } : { access }
}

export async function refreshAccessToken(): Promise<string> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    throw new Error('No refresh token')
  }

  if (isJwtExpired(refreshToken, 0)) {
    throw new Error('Refresh token expired')
  }

  if (!refreshPromise) {
    refreshPromise = axios
      .post(
        apiUrl(REFRESH_PATH),
        { refresh: refreshToken },
        { _skipAuthRefresh: true }
      )
      .then(({ data }) => {
        const tokens = parseRefreshResponse(data)
        persistTokens(tokens)
        return tokens.access
      })
      .finally(() => {
        refreshPromise = null
      })
  }

  return refreshPromise
}

export async function ensureValidAccessToken(): Promise<string | null> {
  const access = getAccessToken()
  const refresh = getRefreshToken()

  if (!refresh) {
    return access
  }

  if (access && !isJwtExpired(access)) {
    return access
  }

  try {
    return await refreshAccessToken()
  } catch {
    return null
  }
}

export function isAuthRefreshRequest(url?: string): boolean {
  if (!url) {
    return false
  }
  return url.includes(REFRESH_PATH) || url.includes(LOGIN_PATH)
}
